import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Rocket, CheckCircle2, XCircle, AlertCircle, RefreshCw, Loader2, Link2, KeyRound, Save, Eye, EyeOff, HardDrive, Database, Download, Upload } from "lucide-react";
import api from "@/lib/api";
import { downloadFile } from "@/lib/download";

const LEVEL = {
  required: { label: "Required", cls: "text-lime-300 border-lime-400/30" },
  recommended: { label: "Recommended", cls: "text-emerald-300 border-emerald-400/30" },
  optional: { label: "Optional", cls: "text-slate-400 border-white/10" },
};

const KEY_GROUPS = [
  { title: "PesaPal", keys: [["pesapal_consumer_key", "Consumer Key"], ["pesapal_consumer_secret", "Consumer Secret"]] },
  { title: "NowPayments (crypto)", keys: [["nowpayments", "API Key"], ["nowpayments_ipn_secret", "IPN Secret"]] },
  { title: "Telegram (signals)", keys: [["telegram_bot_token", "Bot Token"], ["telegram_channel_id", "Channel ID"]] },
  { title: "Email", keys: [["resend", "Resend API Key"], ["resend_from", "Resend From (e.g. HK Forexedge <no-reply@yourdomain.com>)"], ["sendgrid", "SendGrid API Key"]] },
  { title: "Captcha", keys: [["turnstile_site_key", "Turnstile Site Key"], ["turnstile_secret", "Turnstile Secret"]] },
];

export default function AdminLaunch() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [keyRows, setKeyRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [revealed, setRevealed] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [storage, setStorage] = useState(null);
  const [storageBusy, setStorageBusy] = useState(false);
  const [ttl, setTtl] = useState("");
  const [ttlSaving, setTtlSaving] = useState(false);
  const [migOpen, setMigOpen] = useState(false);
  const [mig, setMig] = useState({ bucket: "", endpoint_url: "", access_key: "", secret_key: "", region: "us-east-1" });
  const [migBusy, setMigBusy] = useState(false);
  const [migResult, setMigResult] = useState(null);
  const [importMode, setImportMode] = useState("replace");
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const exportBackup = () => {
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "");
    toast.info("Preparing backup…");
    downloadFile("/admin/backup/export", `hkforexedge-backup-${ts}.json.gz`);
  };
  const importBackup = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const warn = importMode === "replace"
      ? "\n\nREPLACE wipes each collection in THIS database before loading the backup. Use it on a fresh target site."
      : "\n\nMERGE upserts records by id/key and keeps anything not in the backup.";
    if (!window.confirm(`Import "${file.name}" in ${importMode.toUpperCase()} mode?${warn}`)) return;
    setImportBusy(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", importMode);
      const { data } = await api.post("/admin/backup/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Imported ${data.total} record(s) across ${Object.keys(data.collections).length} collection(s)`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { setImportBusy(false); }
  };
  const runMigrate = (dry) => {
    if (!mig.bucket || !mig.access_key || !mig.secret_key) { toast.error("Bucket + access key + secret are required"); return; }
    setMigBusy(true); setMigResult(null);
    api.post("/admin/storage/migrate", { ...mig, dry_run: dry }).then((r) => {
      setMigResult(r.data);
      if (dry) toast.success(`Found ${r.data.objects_found} file(s) to copy`);
      else toast.success(`Copied ${r.data.copied} · skipped ${r.data.skipped} · failed ${r.data.failed?.length || 0}`);
    }).catch((e) => toast.error(e?.response?.data?.detail || "Migration failed")).finally(() => setMigBusy(false));
  };

  const testStorage = () => {
    setStorageBusy(true);
    api.get("/admin/storage/status").then((r) => setStorage(r.data)).catch((e) => toast.error(e?.response?.data?.detail || "Storage check failed")).finally(() => setStorageBusy(false));
  };
  const saveTtl = () => {
    const n = parseInt(ttl, 10);
    if (!n || n < 60 || n > 604800) { toast.error("Enter 60–604800 seconds"); return; }
    setTtlSaving(true);
    api.put("/admin/settings/signed-url", { ttl_seconds: n }).then(() => toast.success("Signed-link validity saved")).catch((e) => toast.error(e?.response?.data?.detail || "Failed")).finally(() => setTtlSaving(false));
  };

  const load = () => {
    setLoading(true);
    api.get("/admin/launch-readiness").then((r) => setData(r.data)).catch((e) => toast.error(e?.response?.data?.detail || "Failed")).finally(() => setLoading(false));
    api.get("/admin/api-keys").then((r) => setKeyRows(r.data)).catch(() => {});
    api.get("/admin/settings/signed-url").then((r) => setTtl(String(r.data.ttl_seconds))).catch(() => {});
    testStorage();
  };
  useEffect(() => { load(); }, []);

  const keyStatus = (p) => keyRows.find((r) => r.provider === p) || {};
  const saveKey = async (provider) => {
    const value = drafts[provider];
    if (!value) { toast.error("Enter a value first"); return; }
    setSavingKey(provider);
    try {
      await api.put(`/admin/api-keys/${provider}`, { value });
      toast.success("Saved");
      setDrafts((d) => ({ ...d, [provider]: "" }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSavingKey(null); }
  };

  const registerIpn = async () => {
    setRegistering(true);
    try { const { data } = await api.post("/admin/pesapal/register-ipn"); toast.success(`IPN registered (${(data.ipn_id || "").slice(0, 8)}…)`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "IPN registration failed"); }
    finally { setRegistering(false); }
  };

  const checks = data?.checks || [];
  const required = checks.filter((c) => c.level === "required");
  const requiredDone = required.filter((c) => c.ok).length;

  return (
    <div data-testid="admin-launch">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline mb-2">Admin · Deployment</p>
          <h1 className="font-editorial text-3xl font-medium tracking-tight text-white sm:text-4xl">Launch readiness</h1>
          <p className="mt-2 max-w-xl text-sm text-emerald-100/60">Confirm your app is ready for real payments and add every provider key right here — no need to leave this screen.</p>
        </div>
        <button onClick={load} className="btn-edge-ghost text-xs" data-testid="launch-refresh"><RefreshCw size={14} /> Refresh</button>
      </div>

      {loading ? (
        <div className="card-edge p-10 text-center text-emerald-100/50">Checking…</div>
      ) : (
        <>
          <div className={`mb-6 flex flex-wrap items-center justify-between gap-4 rounded-xl border-2 p-6 ${data.ready ? "border-lime-400/40 bg-lime-400/5" : "border-amber-400/30 bg-amber-400/5"}`} data-testid="launch-status">
            <div className="flex items-center gap-3">
              <Rocket size={22} className={data.ready ? "text-lime-300" : "text-amber-300"} />
              <div>
                <p className="font-editorial text-xl text-white">{data.ready ? "Ready to go live" : "Not ready yet"}</p>
                <p className="text-sm text-emerald-100/60">{requiredDone}/{required.length} required checks passing</p>
              </div>
            </div>
          </div>

          <div className="mb-6 grid gap-3 sm:grid-cols-2" data-testid="launch-urls">
            {[["Public backend URL", data.public_backend_url, "PUBLIC_BACKEND_URL"], ["Frontend URL", data.frontend_url, "FRONTEND_URL"]].map(([label, val, envk]) => (
              <div key={envk} className="rounded-lg border border-emerald-500/12 bg-[#0d2416]/50 p-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-emerald-100/50">{label}</p>
                <p className="mt-1 flex items-center gap-1.5 truncate text-sm text-white"><Link2 size={12} className="shrink-0 text-emerald-300" />{val || "not set"}</p>
                <p className="mt-1 text-[11px] text-emerald-100/40">Set <span className="font-mono-edge">{envk}</span> in your deployment environment (see docs/INSTALL.md).</p>
              </div>
            ))}
          </div>

          <div className="mb-6 rounded-lg border border-emerald-500/12 bg-[#0d2416]/50 p-5" data-testid="launch-storage">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2"><HardDrive size={16} className="text-lime-300" /><p className="overline">File storage</p></div>
              <button onClick={testStorage} disabled={storageBusy} data-testid="launch-storage-test" className="btn-edge-ghost text-xs">
                {storageBusy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Test my S3/MinIO connection
              </button>
            </div>
            {storage ? (
              <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm" data-testid="launch-storage-result">
                <span className="flex items-center gap-2">{storage.writable ? <CheckCircle2 size={16} className="text-lime-300" /> : <XCircle size={16} className="text-amber-300" />}<span className="text-white">{storage.writable ? "Read/write OK" : "Not writable"}</span></span>
                <span className="text-emerald-100/70">Backend: <span className="font-mono-edge text-white">{storage.backend}</span></span>
                {storage.bucket && <span className="text-emerald-100/70">Bucket: <span className="font-mono-edge text-white">{storage.bucket}</span></span>}
                {storage.endpoint && <span className="text-emerald-100/70">Endpoint: <span className="font-mono-edge text-white">{storage.endpoint}</span></span>}
                {storage.error && <span className="text-amber-300">{storage.error}</span>}
              </div>
            ) : (
              <p className="mt-3 text-sm text-emerald-100/50">{storageBusy ? "Testing…" : "Runs a write→read→delete self-test against your object store (Emergent / S3 / MinIO)."}</p>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-emerald-500/10 pt-4">
              <span className="text-sm text-emerald-100/70">Signed download-link validity</span>
              <input type="number" min="60" max="604800" value={ttl} onChange={(e) => setTtl(e.target.value)} data-testid="signed-ttl-input"
                className="w-28 rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-1.5 text-sm text-white" />
              <span className="text-sm text-emerald-100/50">seconds{ttl ? ` (~${Math.round(Number(ttl) / 60)} min)` : ""}</span>
              <button onClick={saveTtl} disabled={ttlSaving} data-testid="signed-ttl-save" className="btn-edge-ghost text-xs">
                {ttlSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
              </button>
              <p className="w-full text-[11px] text-emerald-100/40">Applies to <code>s3://</code> product links (MinIO/AWS/R2/Spaces). Shorter = harder to re-share; longer = friendlier for large downloads.</p>
            </div>
            <div className="mt-4 border-t border-emerald-500/10 pt-4">
              <button onClick={() => setMigOpen((v) => !v)} data-testid="storage-migrate-toggle" className="text-sm font-medium text-lime-300 hover:underline">
                {migOpen ? "▾" : "▸"} Migrate files from current storage → my S3/MinIO
              </button>
              {migOpen && (
                <div className="mt-3 grid gap-3 sm:grid-cols-2" data-testid="storage-migrate-form">
                  {[
                    ["bucket", "Bucket name", "Required", "e.g. hkforexedge-files — create this bucket in your S3/MinIO console first"],
                    ["endpoint_url", "Endpoint URL", "Blank for AWS", "MinIO: https://minio.yourserver.com · Cloudflare R2: https://<account>.r2.cloudflarestorage.com · DO Spaces: https://<region>.digitaloceanspaces.com · leave empty for AWS S3"],
                    ["access_key", "Access key ID", "Required", "The access key issued by your storage provider"],
                    ["secret_key", "Secret access key", "Required", "Paired with the access key · stored encrypted, shown as dots"],
                    ["region", "Region", "Optional", "e.g. us-east-1 · MinIO accepts any value (us-east-1 is a safe default)"],
                  ].map(([k, label, tag, hint]) => (
                    <div key={k} className={k === "endpoint_url" ? "sm:col-span-2" : ""}>
                      <label htmlFor={`mig-${k}`} className="mb-1 flex flex-wrap items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-emerald-100/60">
                        {label}
                        <span className="rounded-full border border-emerald-500/20 px-1.5 py-0.5 text-[8px] normal-case tracking-normal text-emerald-100/50">{tag}</span>
                      </label>
                      <input id={`mig-${k}`} type={k === "secret_key" ? "password" : "text"} placeholder={label} value={mig[k]}
                        onChange={(e) => setMig({ ...mig, [k]: e.target.value })} data-testid={`mig-${k}`}
                        className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white placeholder:text-emerald-100/30 focus:border-lime-400/60 focus:outline-none" />
                      <p className="mt-1 text-[10px] leading-snug text-emerald-100/40">{hint}</p>
                    </div>
                  ))}
                  <div className="flex items-center gap-2 sm:col-span-2">
                    <button onClick={() => runMigrate(true)} disabled={migBusy} data-testid="mig-dryrun" className="btn-edge-ghost text-xs">
                      {migBusy ? <Loader2 size={13} className="animate-spin" /> : null} Dry run
                    </button>
                    <button onClick={() => runMigrate(false)} disabled={migBusy} data-testid="mig-run" className="btn-edge text-xs">
                      {migBusy ? <Loader2 size={13} className="animate-spin" /> : <HardDrive size={13} />} Copy files
                    </button>
                  </div>
                  {migResult && (
                    <p className="text-[11px] text-emerald-100/70 sm:col-span-2" data-testid="mig-result">
                      {migResult.dry_run
                        ? `Dry run: ${migResult.objects_found} file(s) would be copied.`
                        : `Copied ${migResult.copied} · skipped ${migResult.skipped} · failed ${migResult.failed?.length || 0}.`}
                      {migResult.failed?.length ? ` First error: ${migResult.failed[0].error}` : ""}
                    </p>
                  )}
                  {migResult && !migResult.dry_run && !(migResult.failed && migResult.failed.length) && (
                    <div className="rounded-lg border border-lime-400/30 bg-lime-400/5 p-3 text-[11px] sm:col-span-2" data-testid="mig-switch">
                      <p className="mb-2 font-semibold text-lime-300">Migration done — now switch your app to this bucket:</p>
                      <p className="mb-2 text-emerald-100/70">Set these in your backend deployment environment, then restart the backend:</p>
                      <pre className="mb-2 overflow-x-auto rounded bg-black/40 p-2 font-mono-edge text-emerald-100/80">{`STORAGE_BACKEND=s3
S3_BUCKET=${mig.bucket}
S3_ENDPOINT_URL=${mig.endpoint_url || "(blank for AWS)"}
S3_ACCESS_KEY=${mig.access_key}
S3_SECRET_KEY=********
S3_REGION=${mig.region || "us-east-1"}`}</pre>
                      <div className="flex flex-wrap items-center gap-2">
                        <button onClick={testStorage} disabled={storageBusy} data-testid="mig-switch-retest" className="btn-edge-ghost text-xs">
                          {storageBusy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Re-run self-test
                        </button>
                        <span className="text-emerald-100/50">After the restart, the “Backend” badge above should read <span className="font-mono-edge text-white">s3</span>.</span>
                      </div>
                    </div>
                  )}
                  <p className="text-[11px] text-emerald-100/40 sm:col-span-2">Reads every stored file from your current backend and writes it to the target bucket under the same key (so existing links keep working). Run a dry run first; existing objects are skipped.</p>
                </div>
              )}
            </div>
          </div>

          {/* Backup & migrate data */}
          <div className="mt-6 card-edge p-5" data-testid="admin-backup-card">
            <div className="mb-3 flex items-center gap-2"><Database size={16} className="text-lime-300" /><p className="overline">Backup &amp; migrate data</p></div>
            <p className="mb-4 text-sm text-emerald-100/55">Download a full snapshot of every database collection (users, products, orders, licences, signals, settings, keys…) as one compressed file — or restore one into this site. Combine this with the storage migration above to move the entire platform to a new server. Uploaded files are moved by the storage migration, not this export.</p>
            <div className="flex flex-wrap items-center gap-3">
              <button onClick={exportBackup} data-testid="backup-export" className="btn-edge text-sm"><Download size={14} /> Export all data</button>
              <select value={importMode} onChange={(e) => setImportMode(e.target.value)} data-testid="backup-import-mode"
                className="rounded-md border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-lime-400/60 focus:outline-none">
                <option value="replace">Replace (fresh target)</option>
                <option value="merge">Merge (upsert)</option>
              </select>
              <label className={`btn-edge-ghost cursor-pointer text-sm ${importBusy ? "pointer-events-none opacity-60" : ""}`} data-testid="backup-import-label">
                {importBusy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Import backup
                <input type="file" accept=".gz,.json" onChange={importBackup} disabled={importBusy} data-testid="backup-import-input" className="hidden" />
              </label>
            </div>
            {importResult && (
              <p className="mt-3 text-[11px] text-emerald-100/70" data-testid="backup-import-result">
                Imported {importResult.total} record(s): {Object.entries(importResult.collections).map(([k, v]) => `${k} (${v})`).join(" · ")}
              </p>
            )}
            <p className="mt-3 text-[11px] text-emerald-100/40">A backup contains sensitive data (hashed passwords, encrypted keys) — store it securely. For server-to-server moves you can also run <span className="font-mono-edge text-emerald-100/70">scripts/export_data.py</span> and <span className="font-mono-edge text-emerald-100/70">import_data.py</span> (see docs/MIGRATION.md).</p>
          </div>

          <div className="mt-8 grid gap-3">
            {checks.map((c) => {
              const lv = LEVEL[c.level] || LEVEL.optional;
              return (
                <div key={c.id} className="flex items-start gap-4 rounded-lg border border-emerald-500/12 bg-[#0d2416]/50 p-4" data-testid={`launch-check-${c.id}`}>
                  <span className="mt-0.5">{c.ok ? <CheckCircle2 size={18} className="text-lime-300" /> : (c.level === "required" ? <XCircle size={18} className="text-amber-300" /> : <AlertCircle size={18} className="text-slate-400" />)}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium text-white">{c.label}</p>
                      <span className={`rounded-full border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-widest ${lv.cls}`}>{lv.label}</span>
                    </div>
                    {!c.ok && <p className="mt-1 text-xs text-emerald-100/55">{c.hint}</p>}
                  </div>
                  {c.id === "pesapal_ipn" && !c.ok && (
                    <button onClick={registerIpn} disabled={registering} data-testid="launch-register-ipn" className="btn-edge shrink-0 text-xs">
                      {registering ? <Loader2 size={13} className="animate-spin" /> : <Rocket size={13} />} Register IPN
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Inline provider credentials */}
          <div className="mt-8" data-testid="launch-keys">
            <div className="mb-3 flex items-center gap-2"><KeyRound size={16} className="text-lime-300" /><p className="overline">Provider credentials</p></div>
            <p className="mb-4 text-sm text-emerald-100/55">Add or rotate keys inline. Saved values are masked; enter a new value to replace. After PesaPal keys + a public URL are set, use “Register IPN” above.</p>
            <div className="grid gap-5">
              {KEY_GROUPS.map((g) => (
                <div key={g.title} className="card-edge p-5" data-testid={`launch-keygroup-${g.title.split(" ")[0].toLowerCase()}`}>
                  <p className="mb-3 text-sm font-semibold text-white">{g.title}</p>
                  <div className="grid gap-3">
                    {g.keys.map(([provider, label]) => {
                      const st = keyStatus(provider);
                      const shown = revealed[provider];
                      return (
                        <div key={provider} className="grid gap-2 md:grid-cols-[180px_1fr_auto] md:items-center">
                          <div className="flex items-center gap-2">
                            <span className={`h-2 w-2 rounded-full ${st.configured ? "bg-lime-400" : "bg-slate-500"}`} />
                            <span className="text-xs text-emerald-100/80">{label}</span>
                          </div>
                          <div className="relative">
                            <input type={shown ? "text" : "password"} value={drafts[provider] || ""}
                              onChange={(e) => setDrafts((d) => ({ ...d, [provider]: e.target.value }))}
                              placeholder={st.configured ? `Current: ${st.masked} — enter new to replace` : "Not set — paste value"}
                              data-testid={`launch-key-input-${provider}`}
                              className="w-full rounded-md border border-emerald-500/15 bg-[#061510]/60 px-3 py-2 pr-9 text-sm text-white placeholder:text-emerald-100/30 focus:border-emerald-400/60 focus:outline-none" />
                            <button type="button" onClick={() => setRevealed((s) => ({ ...s, [provider]: !s[provider] }))} className="absolute right-2 top-1/2 -translate-y-1/2 text-emerald-100/40 hover:text-emerald-100/80">
                              {shown ? <EyeOff size={13} /> : <Eye size={13} />}
                            </button>
                          </div>
                          <button onClick={() => saveKey(provider)} disabled={savingKey === provider} data-testid={`launch-key-save-${provider}`} className="btn-edge text-xs">
                            {savingKey === provider ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

